import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';

const buttonVariants = cva(
  // Every button gets the embossed shadow motion: raised at rest, hover
  // deepens the shadow, :active inverts to pressed-in via .shadow-neu-btn.
  // ghost and link stay flat because inset shadows would fight their intent.
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 disabled:active:translate-y-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'shadow-neu-btn bg-gradient-to-br from-primary/85 to-primary text-primary-foreground hover:brightness-105',
        destructive:
          'shadow-neu-btn bg-gradient-to-br from-destructive/85 to-destructive text-destructive-foreground hover:brightness-105',
        outline:
          'shadow-neu-btn border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary:
          'shadow-neu-btn bg-gradient-to-br from-secondary to-secondary/90 text-secondary-foreground hover:brightness-105',
        ghost:
          'shadow-neu-btn border border-border/60 bg-card text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        success:
          'shadow-neu-btn bg-gradient-to-br from-emerald-500 to-emerald-600 text-white hover:brightness-105',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
